import ReactTooltip from "react-tooltip";

/**
 * Behavior-preserving runtime patch for react-tooltip@4.5.1.
 *
 * Three hot paths in the library scale with the size of the whole document AND with the number
 * of mounted <ReactTooltip> instances, so they multiply against each other. Wide grids are the
 * worst case: ~40 tooltips in the app shell over a 10k+ element DOM. One ReactTooltip.rebuild()
 * then costs hundreds of ms, which is why UI unrelated to the table stalls while a table is on
 * screen -- the Bookmarks dropdown, for instance, calls rebuild() when it opens.
 *
 * This changes only how much work the library repeats, never what it does. Delete this module
 * and its import in src/index.js to return to stock behavior -- which is also the right move
 * once react-tooltip is upgraded, since v5+ removed rebuild() and shares one observer itself.
 */

// getTargetArray() scans the document for shadow roots on every call. Nothing in the app renders
// one today, but CKEditor calls attachShadow, so this is watched rather than assumed: the moment a
// shadow root is created, the patched path falls back to the library's own implementation.
let shadowRootExists = false;

const watchForShadowRoots = () => {
    if (typeof Element.prototype.attachShadow !== "function") return;
    const originalAttachShadow = Element.prototype.attachShadow;
    Element.prototype.attachShadow = function attachShadow(...args) {
        shadowRootExists = true;
        return originalAttachShadow.apply(this, args);
    };
};

// Stock getTargetArray copies document.getElementsByTagName("*") into an array and filters it for
// .shadowRoot -- once per instance, twice per rebuild. With no shadow roots that sweep always
// returns [], so all it does is allocate an array the size of the document. The querySelectorAll
// is the half that actually finds targets, so it stays.
const patchGetTargetArray = () => {
    const proto = ReactTooltip.prototype;
    if (typeof proto.getTargetArray !== "function") return;
    const originalGetTargetArray = proto.getTargetArray;

    proto.getTargetArray = function getTargetArray(id) {
        if (shadowRootExists) return originalGetTargetArray.call(this, id);
        // Selector construction copied verbatim from the library.
        const selector = !id ? "[data-tip]:not([data-for])" : `[data-tip][data-for="${id.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
        return Array.prototype.slice.call(document.querySelectorAll(selector));
    };
};

// rebuild() fans out synchronously to every mounted instance, and the app calls it from ~90 places,
// often several times for one interaction. It only re-binds listeners onto DOM that already exists,
// so deferring a burst to the next frame is not observable: a tooltip cannot be hovered before the
// frame it is painted in. show() and hide() stay synchronous.
const patchRebuild = () => {
    const originalRebuild = ReactTooltip.rebuild;
    if (typeof originalRebuild !== "function") return;

    let pending = false;
    ReactTooltip.rebuild = function rebuild() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => {
            pending = false;
            originalRebuild.call(ReactTooltip);
        });
    };
};

// Every instance observes the whole document with {childList, subtree} purely to notice that its
// own current target was removed. The browser allocates a separate set of MutationRecords per
// observer, so N observers make every DOM change N times more expensive to record -- and a table
// re-render is thousands of changes. One shared observer does the same job; collecting the removed
// nodes into a Set first also makes dispatch O(mutations + instances) rather than the product.
const patchRemovalTracker = () => {
    const proto = ReactTooltip.prototype;
    if (typeof proto.bindRemovalTracker !== "function") return;

    const trackedInstances = new Set();
    let sharedObserver = null;

    const ensureObserver = () => {
        if (sharedObserver) return;
        sharedObserver = new MutationObserver(mutations => {
            if (!trackedInstances.size) return;

            let removedNodes = null;
            for (let i = 0; i < mutations.length; i++) {
                const removed = mutations[i].removedNodes;
                for (let j = 0; j < removed.length; j++) {
                    if (!removedNodes) removedNodes = new Set();
                    removedNodes.add(removed[j]);
                }
            }
            if (!removedNodes) return;

            trackedInstances.forEach(instance => {
                const currentTarget = instance.state && instance.state.currentTarget;
                if (currentTarget && removedNodes.has(currentTarget)) instance.hideTooltip();
            });
        });
        sharedObserver.observe(document, { childList: true, subtree: true });
    };

    proto.bindRemovalTracker = function bindRemovalTracker() {
        ensureObserver();
        trackedInstances.add(this);
        // The library only ever checks this for truthiness before unbinding.
        this.removalTracker = sharedObserver;
    };

    proto.unbindRemovalTracker = function unbindRemovalTracker() {
        trackedInstances.delete(this);
        this.removalTracker = null;
        // Stock left zero observers once the last instance unmounted; keeping one attached would
        // still cost a MutationRecord per DOM change for nothing.
        if (!trackedInstances.size && sharedObserver) {
            sharedObserver.disconnect();
            sharedObserver = null;
        }
    };
};

let applied = false;

export const applyReactTooltipPerfPatch = () => {
    if (applied) return;
    applied = true;
    watchForShadowRoots();
    patchGetTargetArray();
    patchRebuild();
    patchRemovalTracker();
};

export default applyReactTooltipPerfPatch;
